import {
  INSTALLER_COLLECTIONS,
  recommendedFor,
  type CrowdsecHub,
  type CrowdsecMode,
  type RecommendedCollection,
  type Role,
  type SettingsPatch,
} from '@bartizo/shared';

/**
 * One server's CrowdSec collections as its page shows them (P11.9.2): what is
 * installed and who put it there, which watch, what the settings still wait
 * for, and what would suit the server. Worked out from the agent's report and
 * the server's settings; nothing here decides anything on the server.
 */

export type CollectionSource = 'settings' | 'installer' | 'other';

export interface CollectionRow {
  name: string;
  version: string | null;
  /** Installed now, as the agent last saw. False: named in the settings, not there yet. */
  installed: boolean;
  source: CollectionSource;
  tainted: boolean;
  outdated: boolean;
  /** What the merged settings say: watch, enforce, or nothing said. */
  mode: CrowdsecMode['mode'] | null;
  /** Named by this server's own layer, so it can be removed here. */
  ownLayer: boolean;
  /** Its mode is set by this server's own layer. */
  ownMode: boolean;
  advice: RecommendedCollection | undefined;
}

export interface CollectionsView {
  rows: CollectionRow[];
  /** Suited to the server's roles and neither installed nor asked for. */
  suggestions: RecommendedCollection[];
}

export function collectionsView(input: {
  hub: CrowdsecHub | null;
  roles: readonly Role[];
  /** The merged document's `modules.crowdsec`. */
  merged: { collections: readonly string[]; modes: readonly CrowdsecMode[] } | undefined;
  /** This server's own layer's `modules.crowdsec`. */
  own:
    | { collections?: readonly string[] | undefined; modes?: readonly CrowdsecMode[] | undefined }
    | undefined;
}): CollectionsView {
  const settings = new Set(input.merged?.collections ?? []);
  const modes = new Map((input.merged?.modes ?? []).map((entry) => [entry.name, entry.mode]));
  const ownCollections = new Set(input.own?.collections ?? []);
  const ownModes = new Set((input.own?.modes ?? []).map((entry) => entry.name));
  const recommended = recommendedFor(['base', ...input.roles]);
  const adviceOf = (name: string) => recommended.find((entry) => entry.name === name);

  const rows: CollectionRow[] = [];
  const seen = new Set<string>();
  for (const item of input.hub?.collections ?? []) {
    seen.add(item.name);
    rows.push({
      name: item.name,
      version: item.version,
      installed: true,
      source: settings.has(item.name)
        ? 'settings'
        : INSTALLER_COLLECTIONS.includes(item.name)
          ? 'installer'
          : 'other',
      tainted: item.tainted,
      outdated: item.outdated,
      mode: modes.get(item.name) ?? null,
      ownLayer: ownCollections.has(item.name),
      ownMode: ownModes.has(item.name),
      advice: adviceOf(item.name),
    });
  }
  for (const name of settings) {
    if (seen.has(name)) continue;
    seen.add(name);
    rows.push({
      name,
      version: null,
      installed: false,
      source: 'settings',
      tainted: false,
      outdated: false,
      mode: modes.get(name) ?? null,
      ownLayer: ownCollections.has(name),
      ownMode: ownModes.has(name),
      advice: adviceOf(name),
    });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return { rows, suggestions: recommended.filter((entry) => !seen.has(entry.name)) };
}

/**
 * This server's layer with one collection added or taken out, or one name's mode
 * set — everything else in the layer exactly as it was.
 */
export function withCrowdsec(
  layer: SettingsPatch,
  change:
    | { kind: 'add'; name: string; watch: boolean }
    | { kind: 'remove'; name: string }
    | { kind: 'mode'; name: string; mode: CrowdsecMode['mode'] },
): SettingsPatch {
  const crowdsec = { ...(layer.modules?.crowdsec ?? {}) };
  let collections = (crowdsec.collections ?? []).filter((name) => name !== change.name);
  // A collection's mode goes with it when it is removed: a watch for a collection
  // nobody asks for any more is noise.
  const modes = (crowdsec.modes ?? []).filter((entry) => entry.name !== change.name);
  switch (change.kind) {
    case 'add':
      collections = [...collections, change.name];
      if (change.watch) modes.push({ name: change.name, mode: 'watch' });
      break;
    case 'remove':
      break;
    case 'mode':
      collections = crowdsec.collections ?? [];
      modes.push({ name: change.name, mode: change.mode });
      break;
  }
  crowdsec.collections = [...collections].sort();
  crowdsec.modes = modes.sort((a, b) => a.name.localeCompare(b.name));
  if (crowdsec.collections.length === 0) delete crowdsec.collections;
  if (crowdsec.modes.length === 0) delete crowdsec.modes;
  return { ...layer, modules: { ...layer.modules, crowdsec } };
}
